import { Controller } from "@hotwired/stimulus";

const STORAGE_KEY = "fiolo:consent";

// `gtag("config")` already reports the page analytics loads on; when that
// happens before the initial `turbo:load`, that event must be skipped so the
// first page is not counted twice.
let initialTurboLoadDone = false;
document.addEventListener("turbo:load", () => { initialTurboLoadDone = true; }, { once: true });

// GDPR cookie consent. Google Analytics is loaded only after the visitor
// accepts; the choice is persisted so the banner shows once.
export default class extends Controller {
  static targets = ["banner"];
  static values = { gaId: String, metaPixelId: String, linkedinPartnerId: String };

  connect() {
    const consent = this.storedConsent();
    // Assign both ways: Turbo cache restores can resurrect a snapshot taken
    // while the banner was visible, so hiding must be explicit too.
    this.bannerTarget.hidden = consent === "granted" || consent === "denied";
    if (consent === "granted") {
      this.loadTrackers();
    }
  }

  accept() {
    this.storeConsent("granted");
    this.bannerTarget.hidden = true;
    this.loadTrackers();
  }

  // Analytics + marketing trackers, all behind consent. Each no-ops unless its
  // id is configured, so nothing loads until the relevant id is set in prod.
  loadTrackers() {
    this.loadAnalytics();
    this.loadMarketing();
  }

  decline() {
    this.storeConsent("denied");
    this.bannerTarget.hidden = true;
  }

  loadAnalytics() {
    if (!this.gaIdValue || window.fioloAnalyticsLoaded) {return;}

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", this.gaIdValue);
    this.trackTurboPageViews();

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaIdValue}`;
    document.head.appendChild(script);
    window.fioloAnalyticsLoaded = true;
    // Lets pending impressions (impression_controller) report events whose
    // visibility happened before consent was granted.
    window.dispatchEvent(new CustomEvent("fiolo:analytics-ready"));
  }

  // Marketing pixels (Meta, LinkedIn). Loaded only after consent and only when
  // the corresponding id is configured via ENV (blank otherwise). The same id
  // (MarketingPixels) drives the enforced CSP, which allowlists each pixel's
  // domains exactly when its id is set — see config/initializers/content_security_policy.rb —
  // so the scripts and beacons below are never blocked by the policy.
  loadMarketing() {
    if (window.fioloMarketingLoaded) {return;}
    let loaded = false;
    if (this.hasMetaPixelIdValue && this.metaPixelIdValue) {
      this.loadMetaPixel(this.metaPixelIdValue);
      loaded = true;
    }
    if (this.hasLinkedinPartnerIdValue && this.linkedinPartnerIdValue) {
      this.loadLinkedInInsight(this.linkedinPartnerIdValue);
      loaded = true;
    }
    if (loaded) {window.fioloMarketingLoaded = true;}
  }

  loadMetaPixel(pixelId) {
    if (window.fbq) {return;}
    const fbq = function fbq(...args) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
      } else {
        fbq.queue.push(args);
      }
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = window._fbq || fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);

    fbq("init", pixelId);
    fbq("track", "PageView");
  }

  loadLinkedInInsight(partnerId) {
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(partnerId);
    if (!window.lintrk) {
      window.lintrk = function lintrk(action, value) {
        window.lintrk.q.push([action, value]);
      };
      window.lintrk.q = [];
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    document.head.appendChild(script);
  }

  // Turbo Drive swaps the body without a full page load, so `gtag("config")`
  // reports only the page it ran on; report every later visit on `turbo:load`.
  //
  // NOTE: GA4's Enhanced Measurement option "Page changes based on browser
  // history events" must stay DISABLED on the property — Turbo navigations use
  // pushState, so leaving it on would double-count every in-app navigation.
  trackTurboPageViews() {
    let skipInitialLoad = !initialTurboLoadDone;
    document.addEventListener("turbo:load", () => {
      if (skipInitialLoad) {
        skipInitialLoad = false;
        return;
      }
      window.gtag("event", "page_view", {
        page_location: window.location.href,
        page_title: document.title,
      });
    });
  }

  storedConsent() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  storeConsent(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Storage unavailable (private mode) — the banner will reappear next visit.
    }
  }
}
